import type {
  GetMessagesQuery,
  GetMessagesQueryVariables,
} from '#src/graphql/generated';
import type { EditorJsData } from '#src/pages/document/editorjs-types';

import { useApolloClient } from '@apollo/client/react';
import { useEffect } from 'react';

import { toaster } from '#src/components/ui/toaster';
import { ProcessorInstanceStatus } from '#src/graphql/generated';
import { applyAskAiReactStoredProcessorResult } from '#src/pages/document/apply-document-ai-processor-result';
import { persistProcessorFailureAsDocumentRootMessage } from '#src/utils/persist-processor-failure-message';

type DocumentThreadRootMessage = GetMessagesQuery['messages'][number];
type DocumentThreadChildMessage =
  DocumentThreadRootMessage['childMessages'][number];
type DocumentThreadMessage =
  | DocumentThreadRootMessage
  | DocumentThreadChildMessage;

function findMessageInDocumentThreads(
  roots: DocumentThreadRootMessage[],
  messageId: string,
): DocumentThreadMessage | undefined {
  for (const root of roots) {
    if (root.id === messageId) {
      return root;
    }
    for (const child of root.childMessages ?? []) {
      if (child.id === messageId) {
        return child;
      }
    }
  }
  return undefined;
}

function processorInstanceFromMessage(
  message: DocumentThreadMessage | undefined,
):
  | NonNullable<
      DocumentThreadMessage['messageContexts'][number]['processorInstance']
    >
  | undefined {
  if (!message?.messageContexts) {
    return undefined;
  }
  for (const ctx of message.messageContexts) {
    if (ctx.processorInstance) {
      return ctx.processorInstance;
    }
  }
  return undefined;
}

export interface UseDocumentAiEditCompletionOptions {
  documentId: string | undefined;
  sortedMessages: GetMessagesQuery['messages'];
  /** Pending user message ids → thread root id for `onDeferredEditSuccess` (undefined = new root). */
  pendingByMessageId: Record<string, string | undefined>;
  clearPending: (messageId: string) => void;
  onValueUpdate: (value: EditorJsData | null) => void;
  onDeferredEditSuccess?: (threadRootId: string | undefined) => void;
  getMessagesVariables: GetMessagesQueryVariables;
}

/**
 * When document messages refetch (e.g. after `documentMessagesUpdated` subscription), applies
 * completed ask-ai-react processor results for pending composer submissions.
 */
export function useDocumentAiEditCompletion({
  documentId,
  sortedMessages,
  pendingByMessageId,
  clearPending,
  onValueUpdate,
  onDeferredEditSuccess,
  getMessagesVariables,
}: UseDocumentAiEditCompletionOptions): void {
  const apolloClient = useApolloClient();

  useEffect(() => {
    const ids = Object.keys(pendingByMessageId);
    if (!documentId || ids.length === 0) {
      return;
    }

    void (async () => {
      const persistDocError = async (
        message: string,
        instanceId?: string | null,
      ) => {
        const persist = await persistProcessorFailureAsDocumentRootMessage(
          apolloClient,
          {
            documentId,
            errorMessage: message,
            processorInstanceId: instanceId,
            getMessagesVariables,
          },
        );
        if (!persist.ok) {
          toaster.create({
            title: 'Could not save error to messages',
            description: persist.error,
            type: 'warning',
          });
        }
      };

      for (const messageId of ids) {
        const threadRootId = pendingByMessageId[messageId];
        const msg = findMessageInDocumentThreads(sortedMessages, messageId);
        const pi = processorInstanceFromMessage(msg);

        if (!pi) {
          continue;
        }

        if (
          pi.status === ProcessorInstanceStatus.Queued ||
          pi.status === ProcessorInstanceStatus.Running
        ) {
          continue;
        }

        if (pi.status === ProcessorInstanceStatus.Done) {
          if (pi.error != null && String(pi.error).length > 0) {
            await persistDocError(String(pi.error), pi.id);
          } else {
            const applied = await applyAskAiReactStoredProcessorResult(
              pi.value,
              {
                onValueUpdate,
                persistDocError,
                instanceId: pi.id,
              },
            );
            if (applied) {
              onDeferredEditSuccess?.(threadRootId);
            }
          }
          clearPending(messageId);
        }
      }
    })();
  }, [
    apolloClient,
    clearPending,
    documentId,
    getMessagesVariables,
    onDeferredEditSuccess,
    onValueUpdate,
    pendingByMessageId,
    sortedMessages,
  ]);
}
